// TanStack Query hooks for the Program entity (reads).
// Thin TanStack wrapper around ProgramService.getEnrolled.

import { useQuery } from '@tanstack/react-query';
import ProgramService from '@/services/ProgramService';
import { PROGRAM_KEYS } from './keys';

/**
 * Programs (care teams) the current user is enrolled in.
 *
 * `staleTime: Infinity` — enrollment is session-static; the user doesn't get
 * enrolled in a new program mid-session. Matches the legacy one-shot init
 * pattern without manual caching machinery.
 *
 * `retry: false` — auth/network errors fail fast to the consumer's error
 * branch (StrictMode safety is built-in: concurrent renders dedupe to a
 * single queryFn invocation per QueryClient).
 */
export function useEnrolledPrograms() {
  return useQuery({
    queryKey: PROGRAM_KEYS.enrolled(),
    queryFn: ProgramService.getEnrolled,
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * A single program (care team) by id — the env `Constants.PROGRAM_ID` on the
 * landing page. On error, `error` is a `WsError`: `status === 214` (no rights)
 * / `380` (not found) are the two branches the caller renders. `retry: false`
 * so those business errors reach the consumer immediately, not after retries.
 */
export function useProgram(programid: string | number) {
  return useQuery({
    queryKey: PROGRAM_KEYS.byId(programid),
    queryFn: () => ProgramService.getById(programid),
    staleTime: Infinity,
    retry: false,
    enabled: !!programid,
  });
}
